import { parse, format, isValid } from 'date-fns';
import { size } from 'lodash';
import type { TableFilter } from 'table/types';
import type { ComboBoxOption } from 'components/inputs/types';
import { useGetOrgType } from 'features/accessControl/hooks/useOrg';

export type FilterRow = {
  id: string | number;
  column: string;
  text?: string;
  dd: ComboBoxOption[];
  dateFrom?: Date | null;
  dateTo?: Date | null;
  date?: Date | null;
  numberFrom?: number | '';
  numberTo?: number | '';
  fractionFrom?: string | '';
  fractionTo?: string | '';
  checked?: boolean;
};

export const getNewFilterRow = (): FilterRow => ({
  id: crypto.randomUUID(),
  column: '',
  text: '',
  dd: [],
  dateFrom: null,
  dateTo: null,
  numberFrom: '',
  numberTo: '',
  checked: false,
});

export const parseDate = (date: string, formatStr = 'yyyy-MM-dd'): Date | null => {
  let parsed = null;
  if (date) {
    parsed = new Date(parse(date, formatStr, new Date()));
  }
  return isValid(parsed) ? parsed : null;
};

export const formatDate = (date: Date | null | undefined, formatStr = 'yyyy-MM-dd'): string => {
  return date ? format(date, formatStr) : '';
};

export const parseNumber = (num: string | number): number | '' => {
  if (num === '' || num == null) {
    return '';
  }
  if (typeof num === 'string') {
    const float = parseFloat(num);
    if (!isNaN(float)) {
      return float;
    }
    return '';
  }
  return num;
};

const usesStringOptionIds = (filterType: TableFilter['type']) => filterType === 'boolean';

const normalizeStringFilterValue = (value: unknown): string => {
  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }
  return String(value);
};

const toFilterOptionId = (rawValue: string, filterType: TableFilter['type']): string | number => {
  if (!usesStringOptionIds(filterType)) {
    const numeric = Number(rawValue);
    return Number.isNaN(numeric) ? rawValue : Number.parseInt(rawValue, 10);
  }

  if (rawValue === 'true') return '1';
  if (rawValue === 'false') return '0';
  return String(rawValue);
};

// get selected filter as a string
export const getValueForUrl = (
  filter: FilterRow | null,
  filters: TableFilter[]
): string | null | Record<string, string> => {
  if (!filter) {
    return null;
  }
  const selected = filters.find(f => f.id === filter.column);
  if (selected) {
    if (
      (selected.type === 'text' || selected.type === 'description' || selected.type === 'tags') &&
      typeof filter.text === 'string' &&
      filter.text
    ) {
      return filter.text;
    }
    if (selected.type === 'dateRange' && (filter.dateFrom || filter.dateTo)) {
      return `${formatDate(filter.dateFrom)},${formatDate(filter.dateTo)}`;
    }
    if (selected.type === 'date' && filter.date) {
      return `${formatDate(filter?.date)}`;
    }
    if (
      (selected.type === 'dd' && filter.dd?.length) ||
      (selected.type === 'status' && filter.dd?.length) ||
      (selected.type === 'switch' && filter.dd?.length) ||
      (selected.type === 'boolean' && filter.dd?.length) ||
      (selected.type === 'tags-dd' && filter.dd?.length) ||
      (selected.type === 'multiselect' && filter.dd?.length)
    ) {
      const ids = filter.dd?.map(txt => txt.id).join(',');
      const labels = filter.dd?.map(txt => txt.label).join(',');
      const colors = filter.dd?.map(txt => txt.customProps?.color || '').join(',');
      return {
        [selected.id]: ids,
        [`${selected.id}Labels`]: labels,
        [`${selected.id}Colors`]: colors,
      };
    }
    if (selected.type === 'number' && (filter.numberFrom || filter.numberTo)) {
      return `${filter.numberFrom}_${filter.numberTo}`;
    }
    if (selected.type === 'fraction' && (filter.fractionFrom || filter.fractionTo)) {
      return `${filter.fractionFrom || ''}_${filter.fractionTo || ''}`;
    }
    if (selected.bottomSwitch) {
      return String(Boolean(filter?.checked));
    }
  }
  return null;
};

// format the filter for url search params
export const formatForUrl = (
  selected: FilterRow[],
  filters: TableFilter[],
  tableFilter: Record<string, string | null> = {}
) => {
  return filters?.reduce((acc: Record<string, string | null>, filter) => {
    const selectedFilter = selected.find(f => f.column === filter.id) || null;
    const values = getValueForUrl(selectedFilter, filters);
    if (values && typeof values === 'object' && !Array.isArray(values)) {
      Object.entries(values).forEach(([key, value]) => {
        if (value || key in tableFilter) {
          acc[key] = value;
        }
      });
    } else if (values || filter.id in tableFilter) {
      acc[filter.id] = values as string | null;
    }
    // For dropdown types, also clear Labels and Colors params when filter is removed
    if (
      !values &&
      (filter.type === 'dd' ||
        filter.type === 'tags-dd' ||
        filter.type === 'multiselect' ||
        filter.type === 'status' ||
        filter.type === 'switch' ||
        filter.type === 'boolean')
    ) {
      if (`${filter.id}Labels` in tableFilter) {
        acc[`${filter.id}Labels`] = null;
      }
      if (`${filter.id}Colors` in tableFilter) {
        acc[`${filter.id}Colors`] = null;
      }
    }
    return acc;
  }, {});
};

// format the filter from url for form
export const formatForForm = (filterState: Record<string, unknown>, filters: TableFilter[]) => {
  const values: FilterRow[] = [];
  if (size(filterState) > 0) {
    filters.forEach(filter => {
      if (filterState[filter.id] !== undefined && filterState[filter.id] !== null) {
        const value = filterState[filter.id];
        const row = getNewFilterRow();
        row.column = filter.id;
        if (filter.type === 'text' || filter.type === 'description' || filter.type === 'tags') {
          row.text = typeof value === 'string' ? value : String(value);
        }
        if (filter.bottomSwitch) {
          row.checked = value === true || value === 'true';
        }
        if (filter.type === 'dateRange') {
          const [dateFrom, dateTo] = String(value).split(',');
          row.dateFrom = parseDate(dateFrom);
          row.dateTo = parseDate(dateTo);
        }
        if (filter.type === 'date') {
          row.date = parseDate(String(value));
        }
        if (
          filter.type === 'dd' ||
          filter.type === 'switch' ||
          filter.type === 'status' ||
          filter.type === 'boolean' ||
          filter.type === 'tags-dd' ||
          filter.type === 'multiselect'
        ) {
          const serializedValue = usesStringOptionIds(filter.type)
            ? normalizeStringFilterValue(value)
            : String(value);

          row.dd = serializedValue
            .split(',')
            .filter(val => val !== '')
            .map((val, index) => {
              const id = toFilterOptionId(val, filter.type);
              const labels = filterState[`${filter?.id}Labels`];
              const colors = filterState[`${filter?.id}Colors`];
              const label = labels != null ? String(labels).split(',')?.[index] ?? '' : '';
              const color = colors != null ? String(colors).split(',')?.[index] ?? '' : '';
              return {
                id,
                label:
                  filter?.options?.find(o => String(o?.id) === String(id))?.label || label || '',
                customProps: { color: color || 'inherit' },
              };
            });
        }
        if (filter.type === 'number') {
          const [from, to] = String(value).split('_');
          row.numberFrom = parseNumber(from);
          row.numberTo = parseNumber(to);
        }
        if (filter.type === 'fraction') {
          const [from, to] = String(value).split('_');
          row.fractionFrom = from;
          row.fractionTo = to;
        }
        values.push(row);
      }
    });
  }
  return values;
};

// filter out filters not accessable by user
export const useFilters = (filter: TableFilter[] = []) => {
  const orgType = useGetOrgType();
  return filter?.filter(f => !f.orgType || f.orgType === orgType);
};
